import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, EmptyState, ErrorState, Field, Icon, Input, Select, Skeleton, Slider, Textarea } from "@sagot/ui";
import type { KeyboardEvent } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useModels } from "../shared/queries";
import type { AgentConfig, CitationStyle, ModelOption } from "../shared/types";
import { LIMITS, approxWords, type SettingsValues } from "./settings-model";
import { SettingsCard, SwitchRow } from "./settings-ui";

const CITATIONS: readonly CitationStyle[] = ["numbered", "source_names", "hidden"];
const TEMPERATURE_STEP = 0.1;
const CONFIDENCE_STEP = 0.05;

/** Same box as a real model card (border, p-4, a 20px title row, a 20px chip row) so the picker does not jump when the list arrives. */
function ModelCardSkeleton() {
  return (
    <div className="rounded-lg border border-line p-4">
      <div className="flex items-center justify-between gap-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="mt-3 flex gap-1.5">
        <Skeleton className="h-5 w-24" />
      </div>
    </div>
  );
}

/**
 * Arrow keys move the choice through the enabled cards (radio-group keyboard pattern). The handler sits on each focusable radio, not on the
 * group; the group is one tab stop (the chosen card, see `tabStopId`) and Space / Enter choose the focused card.
 */
function moveChoiceWithArrows(event: KeyboardEvent<HTMLButtonElement>): void {
  const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
  if (!forward && event.key !== "ArrowLeft" && event.key !== "ArrowUp") return;
  const group = event.currentTarget.closest('[role="radiogroup"]');
  const choices = Array.from(group?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? []);
  const current = choices.indexOf(event.currentTarget);
  if (current < 0) return;
  event.preventDefault();
  const next = choices[(current + (forward ? 1 : choices.length - 1)) % choices.length];
  next?.focus();
  next?.click();
}

/** The card shown as chosen: the saved one, or the recommended one while nothing is saved. */
function isSelected(model: ModelOption, value: string): boolean {
  return value ? value === model.id : model.is_default;
}

function isEnabled(model: ModelOption, formDisabled: boolean): boolean {
  return !formDisabled && model.allowed;
}

/** The group's single tab stop (roving tabindex): the chosen card, or the first enabled one when the chosen card cannot be reached. */
function tabStopId(options: readonly ModelOption[], value: string, formDisabled: boolean): string | undefined {
  const reachable = options.filter((model) => isEnabled(model, formDisabled));
  return (reachable.find((model) => isSelected(model, value)) ?? reachable[0])?.id;
}

function ModelCards({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const models = useModels();
  const { control } = useFormContext<SettingsValues>();
  if (models.isPending) return <div className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-hidden="true">{[0, 1].map((i) => <ModelCardSkeleton key={i} />)}</div>;
  if (models.isError) return <ErrorState headingLevel={3} title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void models.refetch()} retryLabel={t("common.retry")} />;
  const options: readonly ModelOption[] = models.data;
  if (options.length === 0) return <EmptyState headingLevel={3} icon="cpu" title={t("build.agentSettings.model.noModels")} description={t("build.agentSettings.model.noModelsHint")} />;
  return (
    <Controller
      control={control}
      name="model_id"
      render={({ field }) => (
        <div role="radiogroup" aria-label={t("build.agentSettings.model.title")} className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {options.map((model) => {
            const selected = isSelected(model, field.value);
            return (
              <button
                key={model.id}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={model.id === tabStopId(options, field.value, disabled) ? 0 : -1}
                disabled={!isEnabled(model, disabled)}
                onClick={() => { field.onChange(model.id); }}
                onKeyDown={moveChoiceWithArrows}
                className="rounded-lg border border-line p-4 text-left hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-60 aria-checked:border-primary aria-checked:bg-primary-soft"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold">{model.display_name}</span>
                  {model.is_default ? <Badge tone="brand">{t("build.agentSettings.model.recommended")}</Badge> : <Badge>{t(`build.agentSettings.model.tier.${model.tier}`, { defaultValue: model.tier })}</Badge>}
                </div>
                {model.descriptor ? <p className="mt-1 text-xs text-fg-muted">{model.descriptor}</p> : null}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {model.latency_hint ? <Badge><Icon name="clock" className="size-3" />{model.latency_hint}</Badge> : null}
                  {model.credits_per_message === null ? null : <Badge><Icon name="wallet" className="size-3" />{t("build.agentSettings.model.credits", { count: model.credits_per_message })}</Badge>}
                  {model.allowed ? null : <Badge tone="warning"><Icon name="lock" className="size-3" />{t("build.agentSettings.model.notInPlan")}</Badge>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    />
  );
}

/** The saved model's provider is switched off: the agent answers with the default meanwhile. */
function FallbackNotice({ effective }: { effective: AgentConfig["effective_model"] }) {
  const { t } = useTranslation();
  if (!effective?.is_fallback) return null;
  const model = effective.unavailable_model ?? t("build.agentSettings.model.unavailableUnknown");
  return (
    <div role="status" data-testid="model-fallback-notice" className="flex items-start gap-2.5 rounded-md border border-warning bg-warning-soft p-3 text-sm">
      <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
      <p>{t("build.agentSettings.model.unavailable", { model, fallback: effective.display_name })}</p>
    </div>
  );
}

export function ModelTab({ disabled, config }: { disabled: boolean; config: AgentConfig }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const form = useFormContext<SettingsValues>();
  const maxTokens = useWatch({ control: form.control, name: "max_tokens" });
  const idk = useWatch({ control: form.control, name: "idk_enabled" });
  return (
    <>
      <SettingsCard
        title={t("build.agentSettings.model.title")}
        hint={t("build.agentSettings.model.hint")}
        aside={<AppLink href={consoleRoutes.billing(summary.slug)} className="text-xs font-semibold text-primary-ink hover:underline">{t("build.agentSettings.model.usage")}</AppLink>}
      >
        <FallbackNotice effective={config.effective_model} />
        <ModelCards disabled={disabled} />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Controller control={form.control} name="temperature" render={({ field }) => (
            <Slider label={t("build.agentSettings.model.temperature")} value={field.value} onValueChange={field.onChange} min={0} max={1} step={TEMPERATURE_STEP} formatValue={(v) => v.toFixed(1)} startLabel={t("build.agentSettings.model.precise")} endLabel={t("build.agentSettings.model.creative")} disabled={disabled} />
          )} />
          <Field label={t("build.agentSettings.model.maxTokens")} hint={Number.isFinite(maxTokens) ? t("build.agentSettings.model.maxTokensHint", { words: approxWords(maxTokens) }) : t("build.agentSettings.model.maxTokensRange")} error={form.formState.errors.max_tokens?.message}>
            <Input type="number" min={LIMITS.maxTokensMin} max={LIMITS.maxTokensMax} step={LIMITS.maxTokensStep} disabled={disabled} {...form.register("max_tokens", { valueAsNumber: true })} />
          </Field>
        </div>
      </SettingsCard>
      <SettingsCard title={t("build.agentSettings.model.quality")} flush>
        <div className="divide-y divide-line">
          <SwitchRow name="strict" label={t("build.agentSettings.model.strict")} hint={t("build.agentSettings.model.strictHint")} disabled={disabled} />
          <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <label htmlFor="agent-citation" className="text-sm font-semibold">{t("build.agentSettings.model.citation")}</label>
              <p className="mt-0.5 text-xs text-fg-subtle">{t("build.agentSettings.model.citationHint")}</p>
            </div>
            <Select id="agent-citation" className="sm:w-72" disabled={disabled} {...form.register("citation_style")}>
              {CITATIONS.map((c) => <option key={c} value={c}>{t(`build.agentSettings.model.citations.${c}`)}</option>)}
            </Select>
          </div>
          <SwitchRow name="idk_enabled" label={t("build.agentSettings.model.idk")} hint={t("build.agentSettings.model.idkHint")} disabled={disabled} />
          {idk ? (
            <div className="space-y-4 bg-surface-2 px-5 py-4">
              <Controller control={form.control} name="confidence_threshold" render={({ field }) => (
                <Slider label={t("build.agentSettings.model.confidence")} value={field.value} onValueChange={field.onChange} min={LIMITS.confidenceMin} max={LIMITS.confidenceMax} step={CONFIDENCE_STEP} formatValue={(v) => v.toFixed(2)} startLabel={t("build.agentSettings.model.answerMore")} endLabel={t("build.agentSettings.model.onlySure")} disabled={disabled} />
              )} />
              <Field label={t("build.agentSettings.model.fallback")}>
                <Textarea rows={2} maxLength={LIMITS.fallbackMax} disabled={disabled} {...form.register("fallback_message")} />
              </Field>
            </div>
          ) : null}
          <SwitchRow name="followups" label={t("build.agentSettings.model.followups")} hint={t("build.agentSettings.model.followupsHint")} disabled={disabled} />
        </div>
      </SettingsCard>
    </>
  );
}
